import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";

const CustomCardRoot = styled(Box)(({ theme, ownerState }) => {
  const { palette = {}, borders = {}, boxShadows = {}, borderRadius: borderRadiusTheme = {} } = theme;
  const {
    variant = "contained",
    bgColor = "light",
    color = "dark",
    opacity = 1,
    borderRadius = "xxl", // Changed default to match your design
    shadow = "none",
    coloredShadow = "none",
    hoverEffect = false,
    border = false,
    padding = "16px",
    margin = "0",
  } = ownerState;

  // Background value
  let backgroundValue;
  if (variant === "gradient") {
    const { gradients = {} } = palette;
    backgroundValue = gradients[bgColor]
      ? `linear-gradient(135deg, ${gradients[bgColor].main}, ${gradients[bgColor].state})`
      : palette.background?.default || "#ffffff";
  } else {
    backgroundValue = palette[bgColor]?.main || bgColor;
  }

  // Color value
  const colorValue = palette[color]?.main || color;

  // Border styles
  const borderValue = border ? `1px solid ${palette.gray?.main || "#CFCFCF"}` : "none";

  // Fixed border radius value - now properly checks theme.borders
  const borderRadiusValue =
    (borderRadius && borderRadiusTheme[borderRadius]) ||
    borderRadiusTheme?.medium || // default fallback
    theme.shape?.borderRadius || // MUI default
    '0px';


  // Shadow value
  let boxShadowValue = boxShadows[shadow] || shadow;
  if (coloredShadow && coloredShadow !== "none" && boxShadows.colored?.[coloredShadow]) {
    boxShadowValue = boxShadows.colored[coloredShadow];
  }

  // Hover effect
  const hoverStyles = hoverEffect
    ? {
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: boxShadows.lg || "0px 10px 20px rgba(0, 0, 0, 0.1)",
      },
    }
    : {};

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
    border: borderValue,
    padding,
    margin,
    width: '100%', // Ensure full width
    ...hoverStyles,
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 32px)', // 32px = 16px margin on each side
      margin: '16px',
      padding: '12px',
      borderRadius: theme.shape?.borderRadius || '16px', // Slightly smaller radius on mobile
    },
  };
});

const CustomCard = forwardRef((props, ref) => {
  const {
    variant = "contained",
    bgColor = "light",
    color = "dark",
    opacity = 1,
    borderRadius = "xxl", // Set default to xxl to match your design
    shadow = "none",
    coloredShadow = "none",
    hoverEffect = false,
    border = false,
    padding,
    margin,
    children,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  return (
    <CustomCardRoot
      ref={ref}
      ownerState={{
        variant,
        bgColor,
        color,
        opacity,
        borderRadius,
        shadow,
        coloredShadow,
        hoverEffect,
        border,
        padding,
        margin,
      }}
      sx={sx}
      {...rest}
    >
      {children}
    </CustomCardRoot>
  );
});

CustomCard.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  opacity: PropTypes.number,
  borderRadius: PropTypes.string,
  shadow: PropTypes.string,
  coloredShadow: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "none",
  ]),
  hoverEffect: PropTypes.bool,
  border: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

export default CustomCard;